// ---------------------------------------------------------------------------
// Integration Connections — encrypted credential storage for third-party integrations
// ---------------------------------------------------------------------------
// Sprint 5 Stream C (C1): Generic credential storage for any integration provider.
// Supports OAuth2 (access+refresh tokens) and API key auth types.
// ALL credentials encrypted at rest via @ease/crypto (DEK per field, KEK per tenant).
// NEVER log credentials. NEVER return raw credentials in API responses.
// GDPR erasure: DELETE WHERE tenant_id = X (standard cascade).
// ---------------------------------------------------------------------------

import { pgTable, uuid, text, boolean, integer, jsonb, timestamp, index, uniqueIndex } from "drizzle-orm/pg-core";

const tz = { withTimezone: true } as const;

export const INTEGRATION_AUTH_TYPES = ["oauth2", "api_key", "basic_auth", "custom"] as const;
export type IntegrationAuthType = (typeof INTEGRATION_AUTH_TYPES)[number];

export const INTEGRATION_STATUSES = ["active", "expired", "error", "revoked", "pending"] as const;
export type IntegrationStatus = (typeof INTEGRATION_STATUSES)[number];

export const integrationConnections = pgTable(
  "integration_connections",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    providerId: text("provider_id").notNull(), // hubspot, salesforce, ghl, zoho, shopify, etc.
    providerAccountId: text("provider_account_id"), // External account identifier
    displayName: text("display_name"), // User-friendly label

    // Auth type
    authType: text("auth_type").notNull().default("oauth2"), // IntegrationAuthType

    // Encrypted credentials — enc:v1:... format via @ease/crypto
    encAccessToken: text("enc_access_token"),
    encRefreshToken: text("enc_refresh_token"),
    encApiKey: text("enc_api_key"),
    encApiSecret: text("enc_api_secret"),
    encCustomCredentials: text("enc_custom_credentials"), // JSON string, encrypted

    // Token metadata (NOT encrypted — not sensitive)
    tokenType: text("token_type").default("bearer"),
    scope: text("scope"),
    expiresAt: timestamp("expires_at", tz),
    refreshExpiresAt: timestamp("refresh_expires_at", tz),

    // Status
    status: text("status").notNull().default("pending"), // IntegrationStatus
    lastRefreshedAt: timestamp("last_refreshed_at", tz),
    lastUsedAt: timestamp("last_used_at", tz),
    lastErrorAt: timestamp("last_error_at", tz),
    lastErrorMessage: text("last_error_message"),
    failureCount: integer("failure_count").notNull().default(0),

    // Config
    syncEnabled: boolean("sync_enabled").notNull().default(false),
    syncConfig: jsonb("sync_config").$type<{
      entities?: Array<{ source: string; target: string; direction: "in" | "out" | "bidirectional" }>;
      fieldMapping?: Record<string, string>;
      scheduleMinutes?: number;
      lastSyncAt?: string;
    }>(),

    // Metadata
    providerMetadata: jsonb("provider_metadata").$type<Record<string, unknown>>(),
    createdAt: timestamp("created_at", tz).notNull().defaultNow(),
    updatedAt: timestamp("updated_at", tz).notNull().defaultNow(),
  },
  (table) => [
    index("idx_integration_conn_tenant").on(table.tenantId),
    uniqueIndex("idx_integration_conn_unique").on(table.tenantId, table.providerId, table.providerAccountId),
    index("idx_integration_conn_status").on(table.tenantId, table.status),
    index("idx_integration_conn_provider").on(table.providerId),
  ],
);

// Sync log — tracks each sync execution
export const integrationSyncLog = pgTable(
  "integration_sync_log",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    connectionId: uuid("connection_id").notNull(),
    status: text("status").notNull().default("running"), // running, completed, failed, cancelled
    direction: text("direction").notNull().default("in"), // in, out, bidirectional
    entityType: text("entity_type"), // contacts, deals, etc.
    recordsProcessed: integer("records_processed").notNull().default(0),
    recordsFailed: integer("records_failed").notNull().default(0),
    recordsSkipped: integer("records_skipped").notNull().default(0),
    durationMs: integer("duration_ms"),
    errorMessage: text("error_message"),
    startedAt: timestamp("started_at", tz).notNull().defaultNow(),
    completedAt: timestamp("completed_at", tz),
  },
  (table) => [
    index("idx_sync_log_tenant").on(table.tenantId),
    index("idx_sync_log_connection").on(table.connectionId),
    index("idx_sync_log_started").on(table.tenantId, table.startedAt),
  ],
);
